import { useMutation } from "@apollo/client/react";
import { CloseOutlined, ScanOutlined, UploadOutlined } from "@ant-design/icons";
import { App, Button, Empty, Flex, Image, Masonry, Space, Upload, type UploadProps } from "antd";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AddFileToCollectionDocument,
  type CollectionFileFragment,
  RecognizeLabelDocument,
  RemoveFileFromCollectionDocument,
} from "../../../gql/generated/graphqlSchema.tsx";
import { DeletableImageFile } from "../../../components/DeletableImageFile.tsx";
import { PhoneUploadModal } from "../../../components/PhoneUploadModal.tsx";
import { useSaving } from "../../../hooks/useSaving.tsx";
import { useUploadProgress } from "../../../hooks/useUploadProgress.ts";
import { useIngredient } from "../IngredientContext.tsx";
import { MAX_UPLOAD_SIZE_BYTES, uploadResponseSchema } from "../../../utils/upload.ts";

const ACCEPTED_TYPES = "image/jpeg,image/png,image/webp";

export const IngredientPhotosTab = () => {
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const { ingredient, setIngredient } = useIngredient();
  const { trackSaving } = useSaving();
  const [addFile] = useMutation(AddFileToCollectionDocument);
  const [removeFile] = useMutation(RemoveFileFromCollectionDocument);
  const [recognizeLabel, { loading: recognizing }] = useMutation(RecognizeLabelDocument);

  const { progress: uploadPercent, uploading, trackFile, completeFile, failFile } = useUploadProgress();

  const handleRecognize = useCallback(() => {
    modal.confirm({
      title: t("ingredients.labelIngredients.recognizeConfirmTitle"),
      content: t("ingredients.labelIngredients.recognizeConfirm"),
      okButtonProps: { icon: <ScanOutlined /> },
      cancelButtonProps: { icon: <CloseOutlined /> },
      okText: t("ingredients.labelIngredients.recognize"),
      onOk: () =>
        trackSaving(() =>
          recognizeLabel({
            variables: { ingredientId: ingredient.id, language: i18n.language },
          }).then((r) => setIngredient(r.data?.recognizeLabel.refreshedIngredient)),
        ),
    });
  }, [modal, t, ingredient.id, recognizeLabel, trackSaving, setIngredient, i18n.language]);

  const addStagingFileToCollection = async (stagingFileId: string) => {
    const result = await addFile({
      variables: { collectionId: ingredient.photoCollectionId, stagingFileId },
    });
    if (result.data) {
      setIngredient({ ...ingredient, photos: result.data.addFileToCollection });
    }
  };

  const handleChange: UploadProps["onChange"] = ({ file }) => {
    if (file.status === "uploading") {
      trackFile(file.uid, Math.round(file.percent ?? 0));
    }

    if (file.status === "done") {
      completeFile(file.uid);
      const parsed = uploadResponseSchema.safeParse(file.response);
      if (parsed.success) {
        trackSaving(async () => {
          for (const { stagingFileId } of parsed.data) {
            await addStagingFileToCollection(stagingFileId);
          }
        });
      }
    }

    if (file.status === "error") {
      failFile(file.uid);
      void message.error(t("ingredients.photos.uploadFailed"));
    }
  };

  const handleDelete = useCallback(
    (photoId: string) =>
      trackSaving(() =>
        removeFile({
          variables: { collectionId: ingredient.photoCollectionId, fileId: photoId },
        }).then((result) => {
          if (result.data) {
            setIngredient({ ...ingredient, photos: result.data.removeFileFromCollection });
          }
        }),
      ),
    [ingredient, removeFile, setIngredient, trackSaving],
  );

  const renderPhoto = useCallback(
    (itemInfo: { data: CollectionFileFragment }) => {
      const photo = itemInfo.data;
      return <DeletableImageFile file={photo} onDelete={() => handleDelete(photo.id)} />;
    },
    [handleDelete],
  );

  const masonryItems = useMemo(
    () => ingredient.photos.map((photo) => ({ key: photo.id, data: photo })),
    [ingredient.photos],
  );

  const uploadControls = (
    <Space.Compact>
      <Upload
        accept={ACCEPTED_TYPES}
        action="/i/upload/staging"
        beforeUpload={(file) => {
          if (file.size > MAX_UPLOAD_SIZE_BYTES) {
            void message.error(t("ingredients.photos.tooLarge"));
            return Upload.LIST_IGNORE;
          }
          return true;
        }}
        multiple
        onChange={handleChange}
        pastable
        showUploadList={false}
      >
        <Button color="primary" icon={<UploadOutlined />} loading={uploading} variant="outlined">
          {uploading ? `${t("phone.uploading")} ${uploadPercent}%` : t("ingredients.photos.upload")}
        </Button>
      </Upload>
      <PhoneUploadModal onSelect={(stagingFileId) => trackSaving(() => addStagingFileToCollection(stagingFileId))} />
    </Space.Compact>
  );

  if (ingredient.photos.length === 0) {
    return <Empty description={t("ingredients.photos.empty")}>{uploadControls}</Empty>;
  }

  return (
    <Flex gap="middle" vertical>
      <Space wrap>
        {uploadControls}
        <Button
          color="primary"
          icon={<ScanOutlined />}
          loading={recognizing}
          onClick={handleRecognize}
          variant="outlined"
        >
          {t("ingredients.labelIngredients.recognize")}
        </Button>
      </Space>

      <Image.PreviewGroup>
        <Masonry
          columns={{ xs: 2, sm: 3, md: 4, lg: 5 }}
          gutter={[8, 8]}
          itemRender={renderPhoto}
          items={masonryItems}
        />
      </Image.PreviewGroup>
    </Flex>
  );
};
